import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MemberEmailComposer from "@/components/MemberEmailComposer";
import { AlertTriangle, BookUser, Clock, FileSignature, Mail, ShieldAlert, Timer, Wallet, CheckCircle2, UserX } from "lucide-react";

/**
 * SuperAdminPanel
 * Synthèse opérationnelle pour super-admin / gestionnaire_ge :
 *  - Tâches en retard
 *  - Adhésions impayées (dont >30j)
 *  - Conventions tripartites en attente de signatures
 */
export default function SuperAdminPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailAppel, setEmailAppel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/super-admin");
      setData(data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="rounded-xl bg-white border border-slate-200 p-6 text-sm text-slate-400">Chargement du panneau super-admin…</div>;
  if (!data) return null;

  const k = data.kpis || {};
  const hasAnything = (k.taches_en_retard || 0) + (k.adhesions_impayees || 0) + (k.conventions_en_attente_signature || 0) > 0;

  return (
    <section className="space-y-4" data-testid="super-admin-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#C5A059]" />Pilotage super-admin
        </h3>
        <Button size="sm" variant="ghost" onClick={load} data-testid="refresh-superadmin">Actualiser</Button>
      </div>

      {/* KPIs rapides */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiTile icon={BookUser} label="Membres actifs" value={k.membres_actifs} tone="emerald" />
        <KpiTile icon={Wallet} label="Adhésions impayées" value={k.adhesions_impayees} tone={k.adhesions_impayees > 0 ? "amber" : "slate"} sub={k.adhesions_impayees_30j > 0 ? `${k.adhesions_impayees_30j} > 30 j` : null} />
        <KpiTile icon={Clock} label="Tâches en retard" value={k.taches_en_retard} tone={k.taches_en_retard > 0 ? "rose" : "slate"} />
        <KpiTile icon={FileSignature} label="Conventions à signer" value={k.conventions_en_attente_signature} tone={k.conventions_en_attente_signature > 0 ? "amber" : "slate"} />
        <KpiTile icon={UserX} label="DPAE manquantes" value={k.salaries_sans_dpae} tone={k.salaries_sans_dpae > 0 ? "rose" : "emerald"} />
        <KpiTile icon={CheckCircle2} label={hasAnything ? "Alertes" : "Rien d'urgent"} value={hasAnything ? "⚠" : "✓"} tone={!hasAnything ? "emerald" : "amber"} />
      </div>

      {/* Tableau synthétique */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Tâches en retard */}
        <PanelCard icon={Clock} title="Tâches en retard" count={(data.tasks_overdue || []).length} tone="rose">
          {(data.tasks_overdue || []).length === 0 ? (
            <EmptyLine>Aucune tâche en retard.</EmptyLine>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.tasks_overdue.slice(0, 6).map(t => (
                <li key={t.id} className="py-2 text-sm" data-testid={`sa-task-${t.id}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{t.titre}</span>
                    <Badge variant="outline" className="text-rose-700 border-rose-200 bg-rose-50 shrink-0 text-[10px]">
                      <AlertTriangle className="h-3 w-3 mr-1" />{daysBetween(t.echeance)} j
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500">
                    Assignée à <strong>{t.assignee_name || t.assignee_email}</strong> · échéance {t.echeance}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-2">
            <Button asChild size="sm" variant="link" className="text-xs"><Link to="/app/taches">Voir toutes les tâches →</Link></Button>
          </div>
        </PanelCard>

        {/* Adhésions impayées */}
        <PanelCard icon={Wallet} title="Adhésions non à jour" count={(data.adhesions_impayees || []).length} tone="amber">
          {(data.adhesions_impayees || []).length === 0 ? (
            <EmptyLine>Tous les membres sont à jour.</EmptyLine>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.adhesions_impayees.slice(0, 6).map(a => (
                <li key={a.id} className="py-2 text-sm" data-testid={`sa-impaye-${a.id}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{a.raison_sociale || "—"}</span>
                    {a.overdue_30d
                      ? <Badge className="bg-rose-100 text-rose-700 shrink-0 text-[10px]">{a.days_since_issue}j</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 shrink-0 text-[10px]">{a.days_since_issue}j</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                    <span>{a.reference}</span>
                    <span>· {a.prorata?.ttc?.toLocaleString?.("fr-FR") || "—"} € TTC</span>
                    {a.territoire && <span>· {a.territoire}</span>}
                    <button
                      className="ml-auto text-blue-600 hover:underline inline-flex items-center gap-1"
                      onClick={() => setEmailAppel({ id: a.id, type: "adhesion", reference: a.reference, entreprise_id: a.entreprise_id })}
                      data-testid={`sa-relancer-${a.id}`}
                    >
                      <Mail className="h-3 w-3" />Relancer
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-2">
            <Button asChild size="sm" variant="link" className="text-xs"><Link to="/app/appels">Voir tous les appels →</Link></Button>
          </div>
        </PanelCard>

        {/* Conventions tripartites en attente */}
        <PanelCard icon={FileSignature} title="Signatures tripartites en attente" count={(data.conventions_pending || []).length} tone="amber">
          {(data.conventions_pending || []).length === 0 ? (
            <EmptyLine>Toutes les conventions sont pleinement signées.</EmptyLine>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.conventions_pending.slice(0, 6).map(c => (
                <li key={c.id} className="py-2 text-sm" data-testid={`sa-conv-${c.id}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{c.titre}</span>
                    <Badge className="bg-[#FDF9F1] text-[#A88846] border border-[#C5A059]/40 shrink-0 text-[10px]">
                      {c.signatures_count}/3
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500">
                    {c.raison_sociale || "—"} · {c.date_debut} → {c.date_fin}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1">
                    Manque :
                    {c.missing_roles?.map(r => (
                      <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-2">
            <Button asChild size="sm" variant="link" className="text-xs"><Link to="/app/conventions">Voir toutes les conventions →</Link></Button>
          </div>
        </PanelCard>

        {/* DPAE manquantes — alerte conformité */}
        <PanelCard icon={UserX} title="DPAE manquantes" count={(data.salaries_sans_dpae || []).length} tone="rose">
          {(data.salaries_sans_dpae || []).length === 0 ? (
            <EmptyLine>Tous les salariés ont une DPAE archivée.</EmptyLine>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.salaries_sans_dpae.slice(0, 6).map(s => (
                <li key={s.id} className="py-2 text-sm" data-testid={`sa-dpae-${s.id}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{s.prenom} {s.nom}</span>
                    <Badge className="bg-rose-100 text-rose-700 shrink-0 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />DPAE</Badge>
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                    <span>{s.poste || "—"}</span>
                    {s.raison_sociale && <span>· {s.raison_sociale}</span>}
                    {s.territoire && <span>· {s.territoire}</span>}
                    {s.date_entree && <span>· entré {s.date_entree}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-2">
            <Button asChild size="sm" variant="link" className="text-xs"><Link to="/app/ged">Déposer les DPAE en GED →</Link></Button>
          </div>
        </PanelCard>
      </div>

      <MemberEmailComposer
        open={!!emailAppel}
        onOpenChange={(v) => !v && setEmailAppel(null)}
        defaultEntrepriseId={emailAppel?.entreprise_id}
        attachAppel={emailAppel ? { id: emailAppel.id, type: emailAppel.type, reference: emailAppel.reference } : null}
        onSent={load}
      />
    </section>
  );
}

function daysBetween(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function KpiTile({ icon: Icon, label, value, tone = "slate", sub }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    slate: "border-slate-200 bg-white text-slate-600",
  };
  return (
    <div className={`rounded-xl border p-3 flex items-start gap-2 ${tones[tone] || tones.slate}`}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide opacity-80 truncate">{label}</div>
        <div className="text-xl font-bold leading-none mt-1">{value ?? 0}</div>
        {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function PanelCard({ icon: Icon, title, count, tone, children }) {
  const borders = {
    rose: "border-rose-200",
    amber: "border-amber-200",
    emerald: "border-emerald-200",
  };
  return (
    <div className={`bg-white rounded-xl border p-4 ${borders[tone] || "border-slate-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold flex items-center gap-2 text-slate-900"><Icon className="h-4 w-4 text-slate-500" />{title}</h4>
        <Badge variant="outline" className="text-xs">{count}</Badge>
      </div>
      {children}
    </div>
  );
}

function EmptyLine({ children }) {
  return <div className="text-xs text-slate-400 italic py-3 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />{children}</div>;
}
